"use client";

import { IconCircleCheckFilled, IconCreditCard, IconPencil, IconPlus } from "@tabler/icons-react";
import { XIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useState } from "react";
import { StripeSandboxBanner } from "@/components/stripe-sandbox-banner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useOptionalWelcomeCreditReveal } from "@/components/welcome-credit-reveal";
import { buildFrontendUrl } from "@/lib/billing/frontend-urls";
import { createCreditCheckoutSession } from "@/lib/billing/stripe";
import { useAutoRecharge } from "@/lib/billing/useAutoRecharge";
import { type AutoRechargeSettings, type BillingOverview, useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useCreditTopUp } from "@/lib/billing/useCreditTopUp";
import { useMonthlySpendLimit } from "@/lib/billing/useMonthlySpendLimit";
import { useStripePortal } from "@/lib/billing/useStripePortal";
import { useStripeConfig } from "@/lib/useStripeConfig";
import { CreditAmountOption } from "./billing.credit-amount-option";
import { BillingPaymentMethodBrandIcon } from "./billing.payment-method.brand-icon";

const MIN_TOP_UP_USD = 5;

// Micro-USD values arrive from GraphQL as strings (to avoid 32-bit Int overflow),
// so coerce with Number() before converting to dollars.
const microToUsd = (micro: string | number | null | undefined): number => Number(micro ?? 0) / 1_000_000;
// Returns a string so it can be sent to the String-typed GraphQL micro fields
// without losing precision / overflowing on large amounts.
const usdToMicro = (usd: number): string => String(Math.round(usd * 1_000_000));
const formatUsd = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
const formatWholeUsd = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
const formatUsdFromMicro = (micro: string | number | null | undefined) => formatUsd(microToUsd(micro));
const formatPurchaseAmount = (amount: number) =>
  Number.isInteger(amount) ? formatWholeUsd(amount) : formatUsd(amount);
const sanitizeCurrencyInput = (value: string): string => {
  const sanitized = value.replace(/[^\d.]/g, "");
  const [whole, ...fractionParts] = sanitized.split(".");
  const fraction = fractionParts.join("").slice(0, 2);

  return fractionParts.length > 0 ? `${whole}.${fraction}` : whole;
};

const extractMutationError = (error: unknown): string => {
  if (typeof error === "string" && error.trim() !== "") return error;
  if (!error || typeof error !== "object") return "Something went wrong. Please try again.";

  // graphql-request wraps server errors in response.errors[]
  const response = (error as { response?: { errors?: Array<{ message?: string }> } }).response;
  const firstMessage = response?.errors?.[0]?.message;
  if (firstMessage) return firstMessage;

  const msg = (error as { message?: string }).message;
  return msg ?? "Something went wrong. Please try again.";
};

const isPaymentVerificationRequiredError = (error: unknown): boolean => {
  const message = extractMutationError(error).toLowerCase();
  return (
    message.includes("payment verification required") ||
    message.includes("authentication required") ||
    message.includes("requires authentication") ||
    message.includes("additional authentication")
  );
};

type AddCreditsDialogProps = {
  topUp: ReturnType<typeof useCreditTopUp>;
  onSuccess?: () => void;
  paymentMethods: BillingOverview["payment_methods"];
  invoices: BillingOverview["invoices"];
  monthlySpendLimitMicro?: string | null;
  currentSettings: AutoRechargeSettings;
  /** Custom trigger. Pass `null` to omit the trigger (controlled `open` only). */
  trigger?: ReactElement | null;
  /** Controlled open state. When omitted, the dialog manages its own open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const getPreviousCreditPurchaseAmount = (invoices: BillingOverview["invoices"]): number | null => {
  const previousPaidInvoice = invoices.find((invoice) => invoice.status?.toLowerCase() === "paid");
  const amountCents = previousPaidInvoice?.amount_paid ?? previousPaidInvoice?.total;

  if (amountCents == null || amountCents <= 0) return null;

  const amount = Math.round(amountCents / 100);
  return amount >= MIN_TOP_UP_USD ? amount : null;
};

export const AddCreditsDialog = ({
  topUp,
  onSuccess,
  paymentMethods,
  invoices,
  monthlySpendLimitMicro,
  currentSettings,
  trigger,
  open: openControlled,
  onOpenChange,
}: AddCreditsDialogProps) => {
  const [openUncontrolled, setOpenUncontrolled] = useState(false);
  const isControlled = openControlled !== undefined;
  const open = isControlled ? openControlled : openUncontrolled;
  const previousPurchaseAmount = getPreviousCreditPurchaseAmount(invoices);
  const presetAmounts =
    previousPurchaseAmount != null && ![20, 100, 500].includes(previousPurchaseAmount)
      ? [previousPurchaseAmount, 100, 500].sort((a, b) => a - b)
      : [20, 100, 500];
  const defaultAmount = previousPurchaseAmount ?? 100;
  const [amountUsd, setAmountUsd] = useState(String(defaultAmount));
  const [autoRechargeEnabled, setAutoRechargeEnabled] = useState(currentSettings.enabled);
  const [threshold, setThreshold] = useState(
    currentSettings.threshold_micro ? String(microToUsd(currentSettings.threshold_micro)) : "20",
  );
  const [target, setTarget] = useState(
    currentSettings.target_micro ? String(microToUsd(currentSettings.target_micro)) : "40",
  );
  const { mutate, isPending: isTopUpPending, error: topUpError, reset: resetTopUp } = topUp;
  const {
    mutate: updateAutoRecharge,
    isPending: isAutoRechargePending,
    error: autoRechargeError,
    reset: resetAutoRecharge,
  } = useAutoRecharge();
  const { openPortal } = useStripePortal();
  const { isTestMode, error: stripeConfigError } = useStripeConfig();
  const [isCheckoutRedirecting, setIsCheckoutRedirecting] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const showSandboxBanner = !stripeConfigError && isTestMode;

  const parsedAmount = parseFloat(amountUsd);
  const isValidAmount = !Number.isNaN(parsedAmount) && parsedAmount >= MIN_TOP_UP_USD;
  const thresholdNum = parseFloat(threshold);
  const targetNum = parseFloat(target);
  const isValidAutoRecharge =
    !autoRechargeEnabled ||
    (!Number.isNaN(thresholdNum) &&
      thresholdNum >= 0 &&
      !Number.isNaN(targetNum) &&
      targetNum >= MIN_TOP_UP_USD &&
      targetNum > thresholdNum);
  const isPending = isTopUpPending || isAutoRechargePending || isCheckoutRedirecting;
  const error = checkoutError ?? topUpError ?? autoRechargeError;
  const defaultPaymentMethod = paymentMethods.find((method) => method.is_default) ?? paymentMethods[0];
  const isOtherAmount = !presetAmounts.includes(parsedAmount);

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) {
      setOpenUncontrolled(next);
    }
    onOpenChange?.(next);
    if (!next) {
      setAmountUsd(String(defaultAmount));
      setAutoRechargeEnabled(currentSettings.enabled);
      setThreshold(currentSettings.threshold_micro ? String(microToUsd(currentSettings.threshold_micro)) : "20");
      setTarget(currentSettings.target_micro ? String(microToUsd(currentSettings.target_micro)) : "40");
      setCheckoutError(null);
      setIsCheckoutRedirecting(false);
      resetTopUp();
      resetAutoRecharge();
    }
  };

  const redirectToCheckout = async () => {
    setCheckoutError(null);
    setIsCheckoutRedirecting(true);
    try {
      const billingUrl = buildFrontendUrl("/manage/billing");
      const { url } = await createCreditCheckoutSession({
        amountMicro: usdToMicro(parsedAmount),
        successUrl: billingUrl ? `${billingUrl}?checkout=success` : undefined,
        cancelUrl: billingUrl ? `${billingUrl}?checkout=cancelled` : undefined,
      });
      window.location.assign(url);
    } catch (err) {
      console.error(err);
      setCheckoutError("Failed to start checkout. Please try again.");
      setIsCheckoutRedirecting(false);
    }
  };

  const chargeCredits = () => {
    mutate(
      { amount_micro: usdToMicro(parsedAmount) },
      {
        onSuccess: () => {
          handleOpenChange(false);
          onSuccess?.();
        },
        onError: (error) => {
          // Only fall back to hosted Checkout when Stripe requires customer
          // authentication (e.g. Link one-time code / 3DS). A saved card that
          // simply fails to charge should stay in-dialog with the error — do not
          // bounce the user to Checkout and flash "Payment failed".
          if (isPaymentVerificationRequiredError(error)) {
            resetTopUp();
            void redirectToCheckout();
          }
        },
      },
    );
  };

  const continuePurchase = () => {
    if (paymentMethods.length === 0) {
      resetTopUp();
      void redirectToCheckout();
      return;
    }
    chargeCredits();
  };

  const handleSubmit = () => {
    if (!isValidAmount || !isValidAutoRecharge) return;

    if (autoRechargeEnabled !== currentSettings.enabled || autoRechargeEnabled) {
      updateAutoRecharge(
        {
          enabled: autoRechargeEnabled,
          threshold_micro: autoRechargeEnabled ? usdToMicro(thresholdNum) : null,
          target_micro: autoRechargeEnabled ? usdToMicro(targetNum) : null,
          monthly_limit_micro: autoRechargeEnabled ? currentSettings.monthly_limit_micro : null,
        },
        { onSuccess: continuePurchase },
      );
      return;
    }

    continuePurchase();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger !== null ? (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button size="sm">
              <IconPlus className="size-4" aria-hidden />
              Add more credits
            </Button>
          )}
        </DialogTrigger>
      ) : null}
      <DialogContent variant="narrow" className="rounded-control gap-0 overflow-hidden p-0" showCloseButton={false}>
        {showSandboxBanner ? <StripeSandboxBanner className="border-x-0 border-t-0" /> : null}
        <div className="relative grid gap-4 p-5">
          <DialogClose className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogClose>
          <DialogHeader>
            <DialogTitle>{previousPurchaseAmount == null ? "Start building with Radium" : "Add credits"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-6 py-2">
            <div className="grid grid-cols-4 gap-3">
              {presetAmounts.map((amount) => (
                <CreditAmountOption
                  key={amount}
                  amount={formatWholeUsd(amount)}
                  description={
                    amount === previousPurchaseAmount
                      ? "Last purchase"
                      : amount === 20
                        ? "Starting out"
                        : amount === 100
                          ? "Recommended"
                          : "Building"
                  }
                  selected={parsedAmount === amount}
                  onSelect={() => setAmountUsd(String(amount))}
                />
              ))}
              <CreditAmountOption amount="Other" selected={isOtherAmount} onSelect={() => setAmountUsd("")} />
            </div>
            <p className="-my-3 text-sm text-muted-foreground">All prices are in USD</p>

            {isOtherAmount ? (
              <div className="flex flex-col gap-2">
                <Label htmlFor="credit-amount">Custom amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    id="credit-amount"
                    autoComplete="off"
                    type="number"
                    min={MIN_TOP_UP_USD}
                    step="0.01"
                    placeholder="20.00"
                    value={amountUsd}
                    onChange={(event) => setAmountUsd(sanitizeCurrencyInput(event.target.value))}
                    className="bg-white pl-7 dark:bg-background"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Minimum {formatUsd(MIN_TOP_UP_USD)}</p>
              </div>
            ) : null}

            {paymentMethods.length > 0 ? (
              <div className="border-t border-border pt-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-base font-medium text-foreground">Payment methods</h3>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => void openPortal()}
                    aria-label="Edit payment methods"
                  >
                    <IconPencil className="size-4" aria-hidden />
                  </Button>
                </div>
                {defaultPaymentMethod ? (
                  <div className="mt-4 flex items-center gap-3">
                    <div
                      className="flex size-10 items-center justify-center rounded-control border bg-sidebar text-muted-foreground"
                      aria-hidden
                    >
                      <BillingPaymentMethodBrandIcon
                        brand={defaultPaymentMethod.type === "link" ? "link" : defaultPaymentMethod.brand}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {defaultPaymentMethod.type === "link"
                          ? "Link"
                          : `${defaultPaymentMethod.brand ?? "Card"} ending in ${defaultPaymentMethod.last4 ?? "—"}`}
                      </p>
                      <p className="text-sm text-muted-foreground">Default payment method</p>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="rounded-control border border-border bg-card p-4">
              <div className="flex items-center justify-between text-lg font-medium">
                <span>Total due</span>
                <span className="text-2xl">{isValidAmount ? formatPurchaseAmount(parsedAmount) : "$—"}</span>
              </div>
            </div>

            <div className="rounded-control border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="credit-auto-recharge"
                  size="sm"
                  checked={autoRechargeEnabled}
                  onCheckedChange={setAutoRechargeEnabled}
                />
                <Label htmlFor="credit-auto-recharge" className="cursor-pointer text-sm font-medium">
                  Automatically recharge my credit balance when my credit balance falls below my threshold.
                </Label>
              </div>
            </div>

            {autoRechargeEnabled ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="credit-threshold">When credit balance goes below</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                      <Input
                        id="credit-threshold"
                        type="number"
                        min="0"
                        step="1"
                        value={threshold}
                        onChange={(event) => setThreshold(event.target.value.replace(/\D/g, ""))}
                        className="bg-white pl-7 dark:bg-background"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="credit-target">Bring credit balance up to</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                      <Input
                        id="credit-target"
                        type="number"
                        min={MIN_TOP_UP_USD}
                        step="1"
                        value={target}
                        onChange={(event) => setTarget(event.target.value.replace(/\D/g, ""))}
                        className="bg-white pl-7 dark:bg-background"
                      />
                    </div>
                  </div>
                </div>
                <p className="-mt-3 text-sm text-muted-foreground">
                  {monthlySpendLimitMicro != null
                    ? "Adjust your monthly spend limit in Billing"
                    : "Set a monthly spend limit in Billing"}
                </p>
              </>
            ) : null}

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{extractMutationError(error)}</AlertDescription>
              </Alert>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              className="rounded-control"
              width="full"
              onClick={handleSubmit}
              disabled={!isValidAmount || !isValidAutoRecharge || isPending}
            >
              {isCheckoutRedirecting
                ? "Redirecting to checkout…"
                : isPending
                  ? "Processing…"
                  : `Buy ${isValidAmount ? formatPurchaseAmount(parsedAmount) : ""} of credits`}
            </Button>
          </DialogFooter>
          <DialogDescription variant="subtle" className="text-center">
            Credits are consumed with API usage, and expire 1 year after purchase.
            <br />
            By clicking “Buy credits” you agree to{" "}
            <a
              href="https://radium.cloud/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-foreground"
            >
              Radium’s Credit Terms
            </a>
          </DialogDescription>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type SpendLimitDialogProps = {
  currentLimitMicro?: string | null;
  isLoading?: boolean;
  trigger?: ReactElement;
};

export const SpendLimitDialog = ({ currentLimitMicro, isLoading = false, trigger }: SpendLimitDialogProps) => {
  const [open, setOpen] = useState(false);
  const [monthlyLimit, setMonthlyLimit] = useState(currentLimitMicro ? String(microToUsd(currentLimitMicro)) : "");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const { mutate, isPending, error, reset } = useMonthlySpendLimit();
  const hasLimit = currentLimitMicro != null;
  const monthlyLimitNum = parseFloat(monthlyLimit);
  const isValid = !Number.isNaN(monthlyLimitNum) && monthlyLimitNum >= MIN_TOP_UP_USD;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setMonthlyLimit(currentLimitMicro ? String(microToUsd(currentLimitMicro)) : "");
      setConfirmRemove(false);
      reset();
    }
  };

  const handleSave = () => {
    if (!isValid) return;
    mutate({ monthly_limit_micro: usdToMicro(monthlyLimitNum) }, { onSuccess: () => setOpen(false) });
  };

  const handleRemove = () => {
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }

    mutate({ monthly_limit_micro: null }, { onSuccess: () => setOpen(false) });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" variant="outline" disabled={isLoading} aria-busy={isLoading}>
            {isLoading ? "Loading…" : hasLimit ? "Adjust limit" : "Set limit"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:min-w-[560px]">
        <DialogHeader>
          <DialogTitle>{hasLimit ? "Edit spend limit" : "Set spend limit"}</DialogTitle>
          <DialogDescription>
            Cap organization API usage for the calendar month. Requests that would exceed this amount are blocked until
            you raise the limit or the month resets.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 py-2">
          <Label htmlFor="monthly-spending-limit">Monthly spending limit</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input
              id="monthly-spending-limit"
              aria-label="Monthly spending limit"
              autoComplete="off"
              type="number"
              min={MIN_TOP_UP_USD}
              step="1"
              value={monthlyLimit}
              onChange={(event) => {
                setMonthlyLimit(event.target.value.replace(/\D/g, ""));
                setConfirmRemove(false);
              }}
              className="pl-7"
            />
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{extractMutationError(error)}</AlertDescription>
            </Alert>
          ) : null}
        </div>
        <DialogFooter>
          {confirmRemove ? (
            <div
              className="animate-in slide-in-from-bottom-2 fade-in flex-1 self-center text-right text-sm text-muted-foreground duration-200 sm:mr-2 sm:whitespace-nowrap"
              role="alert"
            >
              This could result in unexpected charges.
            </div>
          ) : null}
          {hasLimit ? (
            <Button variant={confirmRemove ? "destructive" : "outline"} onClick={handleRemove} disabled={isPending}>
              {confirmRemove ? "Are you sure?" : "Remove limit"}
            </Button>
          ) : null}
          <Button onClick={handleSave} disabled={!isValid || isPending}>
            {isPending ? "Saving…" : hasLimit ? "Update limit" : "Set limit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BillingOverviewTab = () => {
  const { data } = useBillingOverview();
  const { openPortal } = useStripePortal();
  const overview = data?.billing_overview;
  const welcomeReveal = useOptionalWelcomeCreditReveal();

  const topUp = useCreditTopUp();
  const isBalanceSettling = topUp.isPending || topUp.isAwaitingCredit;

  if (!overview) return null;

  const { credit_balance_micro, auto_recharge, billing_complete, billing_suspended, payment_methods } = overview;
  const displayBalanceMicro = welcomeReveal
    ? welcomeReveal.displayBalanceMicro(credit_balance_micro)
    : credit_balance_micro;
  const isAutoRechargeOn = auto_recharge.enabled;
  // billing_complete is DB-backed and can lag behind Stripe when a payment method
  // is added via the billing portal before the webhook is received. Treat a
  // non-empty live payment_methods list as sufficient to show the credits UI.
  const hasPaymentMethod = billing_complete || payment_methods.length > 0;

  return (
    <div className="flex flex-col gap-6">
      {billing_suspended ? (
        <Alert variant="destructive">
          <AlertDescription>
            Billing is suspended due to a payment failure. Update your payment method to restore access.
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="border border-border bg-card">
        <div className="flex flex-col gap-8 px-6 py-6">
          <div className="flex flex-col gap-1">
            {isBalanceSettling ? (
              <Skeleton className="h-12 w-36" aria-label="Updating credit balance" />
            ) : (
              <p className="font-sans text-5xl font-medium tracking-tight text-foreground">
                {formatUsdFromMicro(displayBalanceMicro)}
              </p>
            )}
            <p className="text-sm text-muted-foreground">Remaining balance</p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <h3 className="text-base font-medium text-foreground">Buy credits</h3>
              <p className="max-w-2xl text-sm text-muted-foreground">
                Add a valid credit card to buy credits or set up auto-reload for when your balance drops below a certain
                threshold.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <AddCreditsDialog
                topUp={topUp}
                paymentMethods={payment_methods}
                invoices={overview.invoices}
                monthlySpendLimitMicro={overview.monthly_spend_limit_micro}
                currentSettings={auto_recharge}
              />
              {/* View pricing is temporarily hidden. */}
              {hasPaymentMethod ? null : (
                <Button size="sm" variant="outline" onClick={() => void openPortal(undefined, false)}>
                  <IconCreditCard className="size-4" aria-hidden />
                  Add payment method
                </Button>
              )}
            </div>

            {!hasPaymentMethod ? (
              <p className="text-sm text-muted-foreground">
                A payment method is required before you can add credits. You will be redirected to the billing portal.
              </p>
            ) : null}

            {hasPaymentMethod && isAutoRechargeOn ? (
              <div className="flex items-start gap-3 border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm">
                <IconCircleCheckFilled className="mt-0.5 size-4 shrink-0 text-emerald-500" aria-hidden />
                <div className="flex flex-col gap-1">
                  <p className="font-medium text-emerald-700 dark:text-emerald-400">Auto recharge is on</p>
                  <p className="text-emerald-700/80 dark:text-emerald-400/80">
                    When your credit balance reaches{" "}
                    <span className="font-medium">{formatUsdFromMicro(auto_recharge.threshold_micro)}</span>, your
                    payment method will be charged to bring the balance up to{" "}
                    <span className="font-medium">{formatUsdFromMicro(auto_recharge.target_micro)}</span>.
                    {auto_recharge.monthly_limit_micro ? (
                      <>
                        {" "}
                        The maximum amount of credits that will automatically recharge in a month is{" "}
                        <span className="font-medium">{formatUsdFromMicro(auto_recharge.monthly_limit_micro)}</span>.
                      </>
                    ) : null}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};
